/**
 * livePlaceNames.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * احتياط أسماء الأماكن من الشبكة، لما لا تحمله الملفّات.
 *
 * ملفّات `public/data/places` مبنيّة من `cities500` — أي كل مكان مأهول بـ500 نسمة
 * فأكثر (‏235 ألفاً). وهذا يغطّي كل ما يظهر عند زووم 10 فأدنى. لكن عند التقريب
 * الشديد تبقى مزارع وأحياء وقرى دون هذا الحدّ، وهي ما تُظهره Windy وZoom Earth.
 * تحميلها من ملفّات يعني عشرات الميغابايت لأجل زووم نادر — فتُطلَب عند الحاجة.
 *
 * ── ما يجعل هذا آمناً لا مصدرَ عطل ──────────────────────────────────────────
 * Overpass بنية تطوّعية مجّانية بلا مفتاح، وتخنق من يُثقل عليها. لذلك:
 *
 *   • **لا تُطلَب قبل زووم 11.** الغالبية العظمى من الجلسات لا تصل إليه أصلاً.
 *   • **طلب واحد على الأكثر في كل مرّة**، ويُلغى السابق فور تحرّك الخريطة.
 *   • **المربّع مقرَّب** إلى شبكة 0.1° فالتحريك الصغير يصيب الكاش لا الشبكة.
 *   • **قاطع دائرة**: بعد فشلين متتاليين يتوقّف الاحتياط لبقيّة الجلسة. الخريطة
 *     تبقى كاملة بأسماء الملفّات — تعطُّل هذه الخدمة يُنقِص أسماء صغيرة، ولا يُعطب
 *     شيئاً.
 *
 * البيانات من OpenStreetMap تحت ODbL — وهو المصدر نفسه المذكور أصلاً في لوحة
 * الإسناد لخريطة الأساس، فلا إسناد إضافي مطلوب.
 */

export interface LivePlace {
    lat: number;
    lon: number;
    /** الاسم العربي إن وُجد في `name:ar`، وإلّا سلسلة فارغة. */
    name: string;
    nameEn: string;
    minZoom: number;
    priority: number;
}

/** لا احتياط قبل هذا الزووم — دونه تكفي الملفّات وتفوقها سرعةً. */
export const LIVE_MIN_ZOOM = 11;

const ENDPOINT = 'https://overpass-api.de/api/interpreter';
const CACHE_MAX = 60;
const cache = new Map<string, LivePlace[]>();

let failures = 0;
let disabled = false;
let inFlight: AbortController | null = null;

/*
 * الزووم الذي يظهر عنده كل صنف، وأولويّته. القيم كلّها **أدنى** من أصغر أولوية في
 * الملفّات (18) عمداً: حين تتزاحم مزرعةٌ من الشبكة مع قرية من الملفّ، يفوز الملفّ —
 * فاسمه مُراجَع وله اسم عربي غالباً، واسم الشبكة قد يكون بلغة محلّية فقط.
 */
const PLACE_RANK: Record<string, [number, number]> = {
    city:          [11, 16],
    town:          [11, 15],
    suburb:        [12, 13],
    village:       [12, 12],
    neighbourhood: [13, 10],
    hamlet:        [13, 9],
    isolated_dwelling: [14, 7],
    farm:          [14, 7],
};

/** مفتاح كاش من مربّع مقرَّب إلى 0.1° — تحريكٌ صغير يصيب نفس المفتاح. */
function cellKey(west: number, south: number, east: number, north: number): string {
    const r = (v: number) => Math.round(v * 10) / 10;
    return `${r(west)},${r(south)},${r(east)},${r(north)}`;
}

/**
 * أسماء الأماكن الصغيرة داخل المربّع. تُعيد `[]` بلا رمي في كل حالات الفشل —
 * المتصل يرسم ما لديه من الملفّات ولا يحتاج التعامل مع خطأ.
 */
export async function fetchLivePlaces(
    west: number, south: number, east: number, north: number, zoom: number,
): Promise<LivePlace[]> {
    if (disabled || zoom < LIVE_MIN_ZOOM) return [];

    const key = cellKey(west, south, east, north);
    const hit = cache.get(key);
    if (hit) return hit;

    inFlight?.abort();
    const ctrl = new AbortController();
    inFlight = ctrl;

    const kinds = Object.keys(PLACE_RANK).join('|');
    const query = `[out:json][timeout:12];`
        + `node["place"~"^(${kinds})$"](${south},${west},${north},${east});`
        + `out body 300;`;

    try {
        const res = await fetch(ENDPOINT, {
            method: 'POST',
            body: `data=${encodeURIComponent(query)}`,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`overpass ${res.status}`);
        const json = await res.json() as { elements?: { lat: number; lon: number; tags?: Record<string, string> }[] };

        const out: LivePlace[] = [];
        for (const el of json.elements ?? []) {
            const tags = el.tags;
            if (!tags) continue;
            const rank = PLACE_RANK[tags.place ?? ''];
            // اسمٌ محلّي بلا لاتينية ولا عربية غير مفيد لأيٍّ من لغتَي الواجهة.
            const nameEn = tags['name:en'] || tags.name || '';
            const nameAr = tags['name:ar'] || '';
            if (!rank || (!nameEn && !nameAr)) continue;
            out.push({ lat: el.lat, lon: el.lon, name: nameAr, nameEn, minZoom: rank[0], priority: rank[1] });
        }

        failures = 0;
        cache.set(key, out);
        // طردٌ بسيط بترتيب الإدخال: الأقدم أوّلاً. المفاتيح مربّعات جغرافية، والعودة
        // إلى مربّع قديم أقلّ احتمالاً من البقاء حول الحالي.
        if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value as string);
        return out;
    } catch (err) {
        // الإلغاء ليس فشلاً — الخريطة تحرّكت فحسب، ولا يجوز أن يقرّب من قطع الدائرة.
        if ((err as Error)?.name === 'AbortError') return [];
        if (++failures >= 2) {
            disabled = true;
            console.warn('[livePlaceNames] أُوقف احتياط الأسماء لبقيّة الجلسة بعد فشلين متتاليين.');
        }
        return [];
    } finally {
        if (inFlight === ctrl) inFlight = null;
    }
}
